import React, { Fragment, useEffect, useState } from 'react';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useInfiniteQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import voca from 'voca';

import { useUpdateFavouriteUserFolderMutation } from 'components/hooks/favouriteUserFolderMutations';

import InfoCardLink from 'components/application/cards/InfoCardLink';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  Divider,
  Item,
  SidebarMenu,
  Subsection,
  SubsectionItem,
} from 'components/application/SidebarMenu';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';

import AddIcon from '-!svg-react-loader?name=AddIcon!icons/add.svg';
import DragIndicatorIcon from '-!svg-react-loader?name=DragIndicatorIcon!icons/drag-indicator.svg';
import FilesIcon from '-!svg-react-loader?name=FilesIcon!icons/files.svg';
import FoldersIcon from '-!svg-react-loader?name=FoldersIcon!icons/folders.svg';
import LockIcon from '-!svg-react-loader?name=LockIcon!icons/ic-lock-20.svg';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function ProjectsDashboardSidebarMenu({
  activeSection,
  hasFolders,
  onFolderClick,
  onNewFolderClick,
  onSectionClick,
  selectedFolderId,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const pluralProjectName =
    currentActor.division.attributes.pluralRAMSName || 'RAMS';

  const { folderPolicy } = useProjectsDashboardContext();

  const [currentFavouriteUserFolderIds, setCurrentFavouriteUserFolderIds] =
    useState([]);
  const [draggingFavouriteUserFolderId, setDraggingFavouriteUserFolderId] =
    useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const [loadMoreFavouritesRef, isLoadMoreFavouritesInView] = useInView();
  const [loadMoreRecentsRef, isLoadMoreRecentsInView] = useInView();

  const updateFavouriteUserFolderMutation =
    useUpdateFavouriteUserFolderMutation();

  const {
    data: domainFavouriteUserFolders,
    fetchNextPage: fetchDomainFavouriteUserFoldersNextPage,
    hasNextPage: hasDomainFavouriteUserFoldersNextPage,
    isFetching: isFetchingDomainFavouriteUserFolders,
    isSuccess: isDomainFavouriteUserFoldersFetchSuccess,
  } = useInfiniteQuery({
    enabled: folderPolicy.canIndex && hasFolders,
    getNextPageParam,
    keepPreviousData: true,
    onError: breadBoard.addInedibleToast,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get('/favourite_user_folders', {
        params: { page: pageParam },
      });

      return response.data;
    },
    queryKey: ['folders', 'favourites'],
    select: (data) =>
      formatInfiniteData({
        data,
        resourceTypes: ['folder'],
      }),
  });

  const {
    data: recentUserFolderSelections,
    fetchNextPage: fetchRecentUserFolderSelectionsNextPage,
    hasNextPage: hasRecentUserFolderSelectionsNextPage,
    isFetching: isFetchingRecentUserFolderSelections,
    isSuccess: isRecentUserFolderSelectionsFetchSuccess,
  } = useInfiniteQuery({
    enabled: folderPolicy.canIndex && hasFolders,
    getNextPageParam,
    keepPreviousData: true,
    onError: breadBoard.addInedibleToast,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get('/recent_user_folder_selections', {
        params: { page: pageParam },
      });

      return response.data;
    },
    queryKey: ['folders', 'recents'],
    select: (data) =>
      formatInfiniteData({
        data,
        resourceTypes: ['folder'],
      }),
  });

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const previousIds = [...currentFavouriteUserFolderIds];
      const oldIndex = previousIds.indexOf(active.id);
      const newIndex = previousIds.indexOf(over.id);

      setCurrentFavouriteUserFolderIds(
        arrayMove(previousIds, oldIndex, newIndex),
      );

      updateFavouriteUserFolderMutation.mutate(
        {
          id: active.id,
          favouriteUserFolderParams: { position: newIndex + 1 },
        },
        {
          onError: () => {
            setCurrentFavouriteUserFolderIds(previousIds);
            breadBoard.addInedibleToast();
          },
        },
      );
    }

    setDraggingFavouriteUserFolderId(null);
  };

  const capitalizedPluralProjectName = voca.capitalize(pluralProjectName);

  const isSorting = !!draggingFavouriteUserFolderId;

  const shouldLoadMoreFavourites =
    currentFavouriteUserFolderIds.length > 0 &&
    hasDomainFavouriteUserFoldersNextPage &&
    isLoadMoreFavouritesInView &&
    !isFetchingDomainFavouriteUserFolders;

  if (shouldLoadMoreFavourites) fetchDomainFavouriteUserFoldersNextPage();

  const shouldLoadMoreRecents =
    recentUserFolderSelections?.data.length > 0 &&
    hasRecentUserFolderSelectionsNextPage &&
    isLoadMoreRecentsInView &&
    !isFetchingRecentUserFolderSelections;

  if (shouldLoadMoreRecents) fetchRecentUserFolderSelectionsNextPage();

  const favouriteUserFolderIds = domainFavouriteUserFolders?.data.map(
    (favouriteUserFolder) => favouriteUserFolder.id,
  );

  const [isAccordionOpen, setIsAccordionOpen] = useState(
    ['current', 'archive'].includes(activeSection),
  );

  useEffect(() => {
    if (favouriteUserFolderIds) {
      setCurrentFavouriteUserFolderIds(favouriteUserFolderIds);
    }
  }, [JSON.stringify(favouriteUserFolderIds)]);

  return (
    <SidebarMenu>
      <div className='tw-flex tw-flex-col tw-p-2'>
        <Accordion
          isOpen={isAccordionOpen}
          triggerContent={
            <AccordionTrigger
              isActive={
                !isAccordionOpen &&
                ['current', 'archive'].includes(activeSection)
              }
              isOpen={isAccordionOpen}
              onClick={() => {
                setIsAccordionOpen((prevState) => !prevState);
                if (!['current', 'archive'].includes(activeSection))
                  onSectionClick({ section: 'current' });
              }}
            >
              <FilesIcon height={20} width={20} />
              {capitalizedPluralProjectName}
            </AccordionTrigger>
          }
        >
          <AccordionItem
            isActive={activeSection === 'current'}
            onClick={() => onSectionClick({ section: 'current' })}
          >
            Current {capitalizedPluralProjectName}
          </AccordionItem>
          <AccordionItem
            isActive={activeSection === 'archive'}
            onClick={() => onSectionClick({ section: 'archive' })}
          >
            Archive
          </AccordionItem>
        </Accordion>
      </div>
      <Divider />
      <div className='tw-mt-2 tw-flex tw-flex-col tw-gap-1 tw-overflow-hidden'>
        {folderPolicy.canIndex ?
          <div className='tw-group tw-relative tw-mx-2 tw-rounded-lg tw-bg-white hover:tw-bg-blue-025'>
            <Item
              isActive={activeSection === 'folders'}
              onClick={() => {
                setIsAccordionOpen(false);
                onSectionClick({ section: 'folders' });
              }}
            >
              <FoldersIcon height={20} width={20} />
              Folders
            </Item>
            <button
              className={classNames(
                'tw-absolute tw-right-2 tw-top-1.5 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border-1 tw-border-solid tw-p-1 group-hover:tw-border-blue-050 group-hover:tw-bg-blue-050 group-hover:hover:tw-border-blue-200 group-hover:hover:tw-bg-blue-050',
                activeSection === 'folders' ?
                  'tw-border-blue-050 tw-bg-blue-050'
                : 'tw-border-blue-025 tw-bg-blue-025',
              )}
              onClick={() => {
                setIsAccordionOpen(false);
                onNewFolderClick();
              }}
            >
              <AddIcon height={20} width={20} />
              <span className='tw-sr-only'>Add folder</span>
            </button>
          </div>
        : <div className='tw-mx-2 tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-2.5 tw-text-grey-500'>
            <div className='tw-flex tw-items-center tw-gap-2'>
              <FoldersIcon
                className='[&_path]:tw-fill-grey-500'
                height={20}
                width={20}
              />
              <p className='tw-mb-0 tw-text-l tw-font-semibold'>Folders</p>
            </div>
            <LockIcon
              className='[&_path]:tw-fill-grey-500'
              height={20}
              width={20}
            />
          </div>
        }
        <div className='tw-flex tw-flex-col tw-gap-1 tw-px-4 tw-py-2'>
          {folderPolicy.canIndex ?
            !hasFolders && (
              <p className='tw-mb-0 tw-text-s tw-tracking-wide tw-text-grey-500'>
                Organise {pluralProjectName} for your team using folders.{' '}
                <button
                  className='tw-border-0 tw-bg-white tw-p-0 tw-font-medium tw-text-blue-500'
                  onClick={() => {
                    setIsAccordionOpen(false);
                    onNewFolderClick();
                  }}
                >
                  Create a folder
                </button>{' '}
                to get started.
              </p>
            )
          : <p className='tw-mb-0 tw-text-s tw-tracking-wide tw-text-grey-500'>
              {currentActor.user.attributes.accessType === 'primary' ?
                <>
                  {' '}
                  <a
                    className='tw-font-medium tw-text-blue-500'
                    href='https://intercom.help/hands-hq/en/articles/11071322-unlock-more-with-the-small-teams-plan'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    Upgrade your account to Small teams
                  </a>{' '}
                  to turn on this feature.
                </>
              : <>
                  Speak to your account owner to{' '}
                  <a
                    className='tw-font-medium tw-text-blue-500'
                    href='https://intercom.help/hands-hq/en/articles/11071322-unlock-more-with-the-small-teams-plan'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    upgrade to Small teams
                  </a>
                  .
                </>
              }
            </p>
          }
          <div className='tw-pt-5'>
            <InfoCardLink
              label={folderPolicy.canIndex ? 'new' : 'learnMore'}
              title='Folders explained'
              url='https://intercom.help/hands-hq/en/articles/11154401-folders-organise-and-group-your-rams'
            />
          </div>
        </div>
        {folderPolicy.canIndex && hasFolders && (
          <div className='tw-overflow-y-auto tw-px-2'>
            <Subsection title='Favourites'>
              {isDomainFavouriteUserFoldersFetchSuccess &&
                (domainFavouriteUserFolders.data.length > 0 ?
                  <>
                    <DndContext
                      collisionDetection={closestCenter}
                      modifiers={[
                        restrictToParentElement,
                        restrictToVerticalAxis,
                      ]}
                      onDragEnd={handleDragEnd}
                      onDragStart={(event) =>
                        setDraggingFavouriteUserFolderId(event.active.id)
                      }
                      sensors={sensors}
                    >
                      <SortableContext
                        items={currentFavouriteUserFolderIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className='tw-relative tw-flex tw-flex-col tw-gap-0.5'>
                          {currentFavouriteUserFolderIds.map((id, index) => {
                            const favouriteUserFolder =
                              domainFavouriteUserFolders.data.find(
                                (favouriteUserFolder) =>
                                  favouriteUserFolder.id === id,
                              );

                            if (!favouriteUserFolder) return null;

                            const folder =
                              domainFavouriteUserFolders.included.folder[
                                favouriteUserFolder.relationships.folder.data.id
                              ];

                            const gap = 2;
                            const height = 36;

                            const topStyle =
                              index === 0 ? 0 : index * (gap + height);

                            return (
                              <Fragment key={id}>
                                {isSorting && (
                                  <div
                                    className='tw-absolute tw-h-9 tw-w-full tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-100 tw-bg-blue-025'
                                    style={{ top: topStyle }}
                                  />
                                )}
                                <SortableFavouriteUserFolder
                                  folder={folder}
                                  id={id}
                                  isActive={
                                    activeSection === 'favourites' &&
                                    selectedFolderId === folder.id
                                  }
                                  isDraggable={
                                    !updateFavouriteUserFolderMutation.isLoading
                                  }
                                  isDragging={
                                    draggingFavouriteUserFolderId === id
                                  }
                                  isSorting={isSorting}
                                  onFolderClick={(...args) => {
                                    setIsAccordionOpen(false);
                                    onFolderClick(...args);
                                  }}
                                />
                              </Fragment>
                            );
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                    <div ref={loadMoreFavouritesRef} />
                  </>
                : <p className='tw-mb-0 tw-pl-1 tw-text-s tw-tracking-wide tw-text-grey-500'>
                    Quickly access folders by starring them in the{' '}
                    <button
                      className='tw-border-0 tw-bg-white tw-p-0 tw-font-medium tw-text-blue-500'
                      onClick={() => {
                        setIsAccordionOpen(false);
                        onSectionClick({ section: 'folders' });
                      }}
                    >
                      Folders
                    </button>{' '}
                    tab.
                  </p>)}
            </Subsection>
            <Divider />
            <Subsection title='Recents'>
              {isRecentUserFolderSelectionsFetchSuccess &&
                (recentUserFolderSelections.data.length > 0 ?
                  <>
                    {recentUserFolderSelections.data.map(
                      (recentUserFolderSelection) => {
                        const folder =
                          recentUserFolderSelections.included.folder[
                            recentUserFolderSelection.relationships.folder.data
                              .id
                          ];

                        return (
                          <SubsectionItem
                            isActive={
                              activeSection === 'recents' &&
                              selectedFolderId === folder.id
                            }
                            key={recentUserFolderSelection.id}
                            onClick={() => {
                              setIsAccordionOpen(false);
                              onFolderClick({
                                folderId: folder.id,
                                section: 'recents',
                              });
                            }}
                          >
                            {folder.attributes.name}
                          </SubsectionItem>
                        );
                      },
                    )}
                    <div ref={loadMoreRecentsRef} />
                  </>
                : <p className='tw-mb-0 tw-pl-1 tw-text-s tw-tracking-wide tw-text-grey-500'>
                    <button
                      className='tw-border-0 tw-bg-white tw-p-0 tw-font-medium tw-text-blue-500'
                      onClick={() => {
                        setIsAccordionOpen(false);
                        onSectionClick({ section: 'folders' });
                      }}
                    >
                      Folders
                    </button>{' '}
                    last accessed by you will show up here.
                  </p>)}
            </Subsection>
          </div>
        )}
      </div>
    </SidebarMenu>
  );
}

ProjectsDashboardSidebarMenu.propTypes = {
  activeSection: PropTypes.string.isRequired,
  hasFolders: PropTypes.bool,
  onFolderClick: PropTypes.func.isRequired,
  onNewFolderClick: PropTypes.func.isRequired,
  onSectionClick: PropTypes.func.isRequired,
  selectedFolderId: PropTypes.string,
};

function SortableFavouriteUserFolder({
  folder,
  id,
  isActive,
  isDraggable,
  isDragging,
  isSorting,
  onFolderClick,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ disabled: !isDraggable, id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={classNames(
        'tw-group tw-relative tw-z-[1] tw-rounded-lg',
        isSorting ?
          isDragging ? 'tw-z-[2] tw-bg-blue-025'
          : 'tw-bg-white'
        : 'tw-bg-white hover:tw-bg-blue-025',
      )}
      ref={setNodeRef}
      style={style}
    >
      <div
        className={classNames(
          'tw-absolute -tw-left-3 tw-flex tw-h-9 tw-w-5 tw-touch-none tw-items-center tw-justify-center tw-rounded-sm',
          isSorting ?
            isDragging ? 'tw-cursor-grabbing tw-bg-blue-050 tw-opacity-100'
            : 'hover-hover:tw-opacity-0'
          : 'group-hover:tw-bg-blue-025 group-hover:tw-opacity-100 group-hover:hover:tw-bg-blue-050 group-hover:hover:tw-opacity-100 hover-hover:tw-opacity-0',
        )}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon
          className='[&_path]:tw-fill-grey-700'
          height={12}
          width={8}
        />
      </div>
      <SubsectionItem
        isActive={!isSorting && isActive}
        onClick={() =>
          onFolderClick({
            folderId: folder.id,
            section: 'favourites',
          })
        }
      >
        {folder.attributes.name}
      </SubsectionItem>
    </div>
  );
}

SortableFavouriteUserFolder.propTypes = {
  folder: resourceShape('folder').isRequired,
  id: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  isDraggable: PropTypes.bool,
  isDragging: PropTypes.bool,
  isSorting: PropTypes.bool,
  onFolderClick: PropTypes.func.isRequired,
};
